import React, { useState, useEffect } from 'react'
import { useTranslation } from '@/i18n'
import Toast from '@/components/Toast'

// API 基础配置
const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL

// 获取请求头
const getHeaders = (): Record<string, string> => {
    const token = localStorage.getItem('token') || localStorage.getItem('auth_token') || ''
    return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
    }
}

// API响应类型
interface ApiResponse<T> {
    code: number;
    data: T;
    message: string;
}

// 通用请求处理
async function handleResponse<T>(response: Response): Promise<T> {
    console.log('📋 [handleResponse] Response status:', response.status);

    if (!response.ok) {
        try {
            const errorData = await response.json();
            console.log('📋 [handleResponse] Error response data:', errorData);
            throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
        } catch (jsonError) {
            const errorText = await response.text();
            throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
        }
    }

    try {
        const data = await response.json() as ApiResponse<T>;
        console.log('📥 [handleResponse] Full API response:', data);

        // 根据API文档格式处理响应
        if (data.code !== 200 && data.code !== 0) {
            throw new Error(data.message || 'API request failed');
        }

        return data.data;
    } catch (jsonError) {
        console.error('❌ [handleResponse] JSON parsing error:', jsonError);
        throw new Error('Failed to parse API response');
    }
}

// AI服务配置类型
type AIServiceConfig = {
    service_name: string
    benefit_code: string
    required_amount: number
    description: string
}

type AIServiceConfigs = {
    [key: string]: AIServiceConfig
}

const PricingPage: React.FC = () => {
    const [services, setServices] = useState<AIServiceConfigs | null>(null)
    const [loading, setLoading] = useState(true)
    const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
    const { t } = useTranslation()

    // 获取AI服务配置
    const fetchAIServiceConfigs = async (): Promise<void> => {
        try {
            setLoading(true)
            console.log('🔄 [fetchAIServiceConfigs] Starting API request...')

            const response = await fetch(`${API_BASE_URL}/ai/services/config`, {
                method: 'GET',
                headers: getHeaders()
            })

            console.log('📥 [fetchAIServiceConfigs] API response received:', response)

            const data = await handleResponse<AIServiceConfigs>(response)

            console.log('✅ [fetchAIServiceConfigs] Processed data:', data)
            console.log('✅ [fetchAIServiceConfigs] Data type:', typeof data)

            // 确保数据是有效的对象
            if (data && typeof data === 'object' && Object.keys(data).length > 0) {
                // 转换PascalCase字段名为camelCase
                const normalizedData: AIServiceConfigs = Object.entries(data).reduce((acc: AIServiceConfigs, [key, value]: [string, any]) => {
                    if (value && typeof value === 'object') {
                        // 确保所有字段都有正确的类型和默认值
                        acc[key] = {
                            service_name: String(value.ServiceName || value.service_name || ''),
                            benefit_code: String(value.BenefitCode || value.benefit_code || ''),
                            required_amount: Number(value.RequiredAmount || value.required_amount || 0),
                            description: String(value.Description || value.description || '')
                        };
                    }
                    return acc;
                }, {} as AIServiceConfigs);

                console.log('✅ [fetchAIServiceConfigs] Normalized data:', normalizedData)
                setServices(normalizedData)
            } else {
                console.warn('⚠️ [fetchAIServiceConfigs] API returned empty or invalid data')
                throw new Error('API returned empty or invalid data')
            }
        } catch (error) {
            console.error('❌ [fetchAIServiceConfigs] Error fetching AI service configs:', error)

            // 无论什么错误，都显示模拟数据
            const mockData: AIServiceConfigs = {
                chat: {
                    service_name: 'chat',
                    benefit_code: 'credits',
                    required_amount: 1,
                    description: t('pricing.services.chat')
                },
                image: {
                    service_name: 'image',
                    benefit_code: 'credits',
                    required_amount: 1,
                    description: t('pricing.services.image')
                },
                video: {
                    service_name: 'video',
                    benefit_code: 'credits',
                    required_amount: 10,
                    description: t('pricing.services.video')
                },
                tts: {
                    service_name: 'tts',
                    benefit_code: 'credits',
                    required_amount: 1,
                    description: t('pricing.services.tts')
                }
            }

            console.log('📋 [fetchAIServiceConfigs] Using mock data due to error:', mockData)
            setServices(mockData)

            // 只在非认证错误时显示toast
            const errorMessage = (error as Error).message || ''
            if (!errorMessage.includes('401') && !errorMessage.includes('Unauthorized')) {
                setToast({ message: t('pricing.fetchError'), type: 'error' })
            }
        } finally {
            setLoading(false)
            console.log('🏁 [fetchAIServiceConfigs] Request completed')
        }
    }

    useEffect(() => {
        fetchAIServiceConfigs()
    }, [])

    if (loading) {
        return (
            <div className="max-w-7xl mx-auto px-6 py-12 pb-[100px]">
                <div className="flex justify-center items-center py-8">
                    <div className="w-10 h-10 border-4 border-blue-500/50 border-t-blue-500 rounded-full animate-spin"></div>
                    <span className="ml-3 text-gray-400">{t('common.loading')}</span>
                </div>
            </div>
        )
    }

    return (
        <div className="max-w-7xl mx-auto px-6 py-12 pb-[100px]">
            {/* Header Section */}
            <div className="mb-8">
                <h1 className="text-2xl font-bold mb-2">{t('pricing.title')}</h1>
                <p className="text-gray-400">{t('pricing.subtitle')}</p>
            </div>

            {/* Services Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* 初始化默认显示模拟数据 */}
                {services ? (
                    Object.values(services).length > 0 ? (
                        Object.values(services).map((service) => {
                            return (
                                <div key={service.service_name} className="border border-blue-500/50 rounded-2xl p-6 transition-all hover:shadow-lg">
                                    <div className="text-center">
                                        <h3 className="text-xl font-semibold mb-2 text-white capitalize">
                                            {service.service_name}
                                        </h3>
                                        <p className="text-gray-400 mb-6">{service.description}</p>
                                        <div className="mb-6">
                                            <span className="text-3xl font-bold text-blue-400">{service.required_amount}</span>
                                            <span className="text-gray-400"> {t('pricing.credits')}</span>
                                        </div>
                                        <div className="rounded-xl p-3">
                                            <p className="text-sm text-gray-300">
                                                {t('pricing.benefitType')}: <span className="font-medium text-blue-400">{service.benefit_code}</span>
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div className="col-span-full text-center py-12">
                            <p className="text-gray-400">{t('pricing.noServices')}</p>
                        </div>
                    )
                ) : (
                    <div className="col-span-full text-center py-12">
                        <p className="text-gray-400">{t('common.loading')}</p>
                    </div>
                )}
            </div>

            {/* FAQ Section */}
            <div className="mt-8 border border-blue-500/50 rounded-2xl p-8">
                <h2 className="text-xl font-bold mb-6 text-white">{t('pricing.faq.title')}</h2>
                <div className="space-y-6">
                    <div>
                        <h3 className="text-lg font-semibold mb-2 text-white">{t('pricing.faq.whatAreCredits')}</h3>
                        <p className="text-gray-400">{t('pricing.faq.creditsDescription')}</p>
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold mb-2 text-white">{t('pricing.faq.howToPurchase')}</h3>
                        <p className="text-gray-400">{t('pricing.faq.purchaseDescription')}</p>
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold mb-2 text-white">{t('pricing.faq.creditsExpire')}</h3>
                        <p className="text-gray-400">{t('pricing.faq.expireDescription')}</p>
                    </div>
                </div>
            </div>

            {/* Toast Component */}
            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}
        </div>
    )
}

export default PricingPage
